import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Wallet } from 'lucide-react';
import { z } from 'zod';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE,
  PCMP_EDIT_LIMITS,
  PCMP_PAYMENT_TYPE_LABELS,
} from '../../constants/pcmpProposal.constants';
import { useEditPcmpProposalSection } from '../../hooks/useEditPcmpProposalSection';
import {
  PCMP_PAYMENT_TYPES,
  type PcmpAccountEditPayload,
  type PcmpPaymentType,
  type PcmpProposalDetail,
} from '../../types/pcmpProposal.types';
import {
  blankToNull,
  pcmpEditReasonSchema,
  pcmpOptionalTextSchema,
} from '../../utils/pcmpProposalEditForm';
import {
  buildPcmpAccountPayload,
  isPcmpAccountChanged,
  PCMP_ACCOUNT_FIELD_NAMES,
  pcmpAccountDefaults,
  pcmpAccountFormShape,
} from '../../utils/pcmpAccountForm';
import { PcmpAccountFields } from './PcmpAccountFields';
import { PcmpEditDialog } from './PcmpEditDialog';
import { PcmpEditReasonField } from './PcmpEditReasonField';

const paymentTypeItems = PCMP_PAYMENT_TYPES.map((value) => ({
  value,
  label: PCMP_PAYMENT_TYPE_LABELS[value],
}));

const paymentFormSchema = z
  .object({
    paymentType: z.enum(PCMP_PAYMENT_TYPES),
    ...pcmpAccountFormShape,
    isOnBehalf: z.boolean(),
    onBehalfName: pcmpOptionalTextSchema(PCMP_EDIT_LIMITS.holderName),
    paymentDate: z.string(),
    referenceNo: pcmpOptionalTextSchema(PCMP_EDIT_LIMITS.shortText),
    reason: pcmpEditReasonSchema,
  })
  .superRefine((values, ctx) => {
    if (values.isOnBehalf && values.onBehalfName === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['onBehalfName'],
        message: 'Required when paying someone else',
      });
    }
  });

type PaymentFormValues = z.infer<typeof paymentFormSchema>;

interface PcmpPaymentEditDialogProps {
  proposal: PcmpProposalDetail;
  onClose: () => void;
}

/**
 * Legacy EditPaymentInfo: the payment type (LDS-791), account, on-behalf,
 * payment date and reference.
 */
export function PcmpPaymentEditDialog({
  proposal: openedWith,
  onClose,
}: PcmpPaymentEditDialogProps) {
  // The saved values the account diff compares against, as opened.
  const [proposal] = useState(openedWith);
  const { payment } = proposal;
  const { account } = payment;

  const defaultValues: PaymentFormValues = {
    paymentType: payment.paymentType,
    ...pcmpAccountDefaults(account),
    isOnBehalf: payment.isOnBehalf,
    onBehalfName: payment.onBehalfName ?? '',
    paymentDate: payment.paymentDate ?? '',
    referenceNo: payment.referenceNo ?? '',
    reason: '',
  };
  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues,
  });
  const { mutate, isPending, error } = useEditPcmpProposalSection(
    proposal.proposalId,
    'payment',
    onClose,
  );
  const isOnBehalf = useWatch({ control: form.control, name: 'isOnBehalf' });
  const paymentType = useWatch({ control: form.control, name: 'paymentType' });
  const typeChanged = paymentType !== payment.paymentType;
  const kind = typeChanged
    ? PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE[paymentType]
    : (account?.kind ?? PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE[payment.paymentType]);

  // Another type starts from an empty account; back to the saved type, the saved one.
  function changeType(next: PcmpPaymentType) {
    const restored = next === payment.paymentType ? defaultValues : pcmpAccountDefaults(null);
    for (const field of PCMP_ACCOUNT_FIELD_NAMES) {
      form.setValue(field, restored[field], { shouldDirty: true });
      form.clearErrors(field);
    }
    form.setValue('paymentType', next, { shouldDirty: true });
  }

  /**
   * A changed account goes out complete; an untouched one is left out (kept).
   * After a type change, a blank account is left out too: the API uses the
   * one the teacher last used with that type, or asks for the details.
   */
  function buildAccount(values: PaymentFormValues): PcmpAccountEditPayload | null | undefined {
    if (!kind) return undefined;
    const baseline = typeChanged ? pcmpAccountDefaults(null) : defaultValues;
    if (!isPcmpAccountChanged(kind, values, baseline)) return undefined;
    return buildPcmpAccountPayload(kind, values, (field, message) =>
      form.setError(field, { message }),
    );
  }

  const onSubmit = form.handleSubmit((values) => {
    const accountPayload = buildAccount(values);
    if (accountPayload === null) return;
    mutate({
      ...(typeChanged ? { paymentType: values.paymentType } : {}),
      ...(accountPayload ? { account: accountPayload } : {}),
      isOnBehalf: values.isOnBehalf,
      ...(values.isOnBehalf ? { onBehalfName: values.onBehalfName } : {}),
      paymentDate: values.paymentDate === '' ? null : values.paymentDate,
      referenceNo: blankToNull(values.referenceNo),
      reason: values.reason,
    });
  });

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Edit Payment"
        icon={Wallet}
        capabilities={proposal.capabilities}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        className="sm:max-w-2xl"
      >
        <FormField
          control={form.control}
          name="paymentType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Payment type</FormLabel>
              <Select<PcmpPaymentType>
                items={paymentTypeItems}
                value={field.value}
                onValueChange={(next) => {
                  if (next !== null && next !== field.value) changeType(next);
                }}
              >
                <FormControl>
                  <SelectTrigger className="h-10 w-full sm:w-56">
                    <SelectValue />
                    <SelectIcon />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {paymentTypeItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <p className="text-sm text-muted-foreground">
          {!kind
            ? 'Cash and gift payments take no account.'
            : typeChanged
              ? `Left blank, the teacher's last ${PCMP_PAYMENT_TYPE_LABELS[paymentType]} account is used. Details entered are saved as a new account of the teacher; only this proposal moves to it.`
              : 'A changed account is saved as a new account of the teacher; only this proposal moves to it.'}
        </p>

        {kind ? <PcmpAccountFields control={form.control} kind={kind} /> : null}

        <FormField
          control={form.control}
          name="isOnBehalf"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-3">
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel className="!mt-0">Paid on behalf of the teacher</FormLabel>
            </FormItem>
          )}
        />
        {isOnBehalf ? (
          <FormField
            control={form.control}
            name="onBehalfName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Paid to <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} autoComplete="off" maxLength={PCMP_EDIT_LIMITS.holderName} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="paymentDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Payment date</FormLabel>
                <div className="flex items-center gap-2">
                  <FormControl>
                    <DatePickerInput
                      value={field.value}
                      onChange={field.onChange}
                      className="flex-1"
                    />
                  </FormControl>
                  {field.value ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => field.onChange('')}
                    >
                      Clear
                    </Button>
                  ) : null}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="referenceNo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reference no</FormLabel>
                <FormControl>
                  <Input {...field} autoComplete="off" maxLength={PCMP_EDIT_LIMITS.shortText} />
                </FormControl>
                <FormDescription>Left blank, it is cleared.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <PcmpEditReasonField control={form.control} />
      </PcmpEditDialog>
    </Form>
  );
}
